// Quotations & Proforma Workflow — ZYNX-CRM-SPEC §3.2 & §4.2 (Sales Panel).
//
// Sales writes a quotation, then converts an accepted one into an Invoice Request.
//
// Quotation & Proforma Workflow:
//   1. Quotation Stage: no approval. A quotation is active the moment it is created (internally
//      status = 'approved'). It is edited in place until it is emailed to the client; after that
//      "Revise Quotation" creates a new quotation that replaces it.
//   2. Proforma Stage: Sales creates the Proforma from an active quotation.
//      Tax invoice generation is available from the payment workflow without Proforma approval.

import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCompanyProfile } from '@/features/recruitment/api';
import { toast } from 'react-hot-toast';
import { FileText, Plus, Send, Mail, Lock, Eye, Wallet, Clock, Layers, Edit, CheckSquare, History, ArrowLeft, Trash2, ChevronDown } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { smoothScrollToTop } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR } from '@/shared/utils/currency';
import { useUser } from '@/shared/hooks/useAuth';
import { useProducts } from '@/features/catalog/hooks/useCatalogApi';
// import { useDeals } from '@/features/deals/hooks/useDeals';
import { generateFullQuotationHtml, generateFullProformaInvoiceHtml } from '@/utils/sales/quotationTemplateTokens';
import {
  SalesDocumentWorkspaceDrawer,
  type DocumentWorkspaceData,
} from '@/components/sales/SalesDocumentWorkspaceDrawer';
import {
  useInvoiceRequests,
  useInvoiceRequestActions,
} from '@/hooks/sales/useInvoiceRequests';
import { QuotationCreateForm } from '@/components/sales/QuotationCreateForm';
import { ProformaInvoiceGenerator } from '@/components/sales/ProformaInvoiceGenerator';
import { SendDocumentEmailModal } from '@/components/sales/SendDocumentEmailModal';
import { AccountingSubNav } from '@/components/sales/AccountingSubNav';
import { salesProformaEditPath } from '@/routes/paths';
import { useDocumentMasterData } from '@/shared/hooks/useDocumentMasterData';
import { serviceLineFields } from '@/lib/serviceLines';
import { hasQuotationBeenEmailed, hasRealProformaNumber, isQuotationSuperseded, printedLineNote } from '@jira-clone/shared-types';
import { cn, cardClassName } from '@jira-clone/ui';

/** One row of the quotations list, built from an invoice request (requestType 'quotation'). */
interface Quotation {
  _id: string;
  id?: string;
  documentNumber?: string;
  number?: string;
  quotationNumber?: string;
  date?: string;
  createdAt?: string;
  documentType: string;
  status: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  lines: Array<{
    productId?: string | null;
    description?: string;
    quantity: number;
    unitPrice: number;
    discount?: number;
    discountType?: 'fixed' | 'percent';
    cgstRate?: number;
    sgstRate?: number;
    igstRate?: number;
  }>;
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  notes?: string;
  createdBy?: string;
  _isDirectIR?: boolean;
  // Extra fields carried over from the invoice request.
  [key: string]: any;
}

function inferMilestoneIndex(document: any): number | null {
  const token = String(document?.invoiceNumber || document?.proformaNumber || document?.number || '').match(/\(([IVXLCDM]+|\d+)\)/i)?.[1];
  if (!token) return null;
  const romanValues: Record<string, number> = { I: 0, II: 1, III: 2, IV: 3, V: 4, VI: 5, VII: 6, VIII: 7, IX: 8, X: 9 };
  const upper = token.toUpperCase();
  if (upper in romanValues) return romanValues[upper];
  const numeric = Number(token);
  return Number.isInteger(numeric) && numeric > 0 ? numeric - 1 : null;
}

export function QuotationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: companyProfile } = useCompanyProfile();
  // The saved document does not store the company's state; it is read from the live company profile.
  const companyState: string = (companyProfile as any)?.registeredAddress?.state || '';
  const [activeTab, setActiveTab] = useState<'quotations' | 'queue' | 'lifecycle'>('quotations');
  const [preparedByFilter, setPreparedByFilter] = useState('');
  // Service rates, HSN/SAC and units for a quotation started from a deal come from master data.
  const { data: masterData, isLoading: masterLoading } = useDocumentMasterData();
  const dealPrefillKeyRef = useRef<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const currentUser = useUser();
  const isSuperAdminOrOwner = ['superadmin', 'owner', 'ceo'].includes((currentUser?.role || '').toLowerCase()) || (currentUser?.department || '').toLowerCase() === 'superadmin';
  const userDept = (currentUser?.department || '').toLowerCase();
  const userRole = (currentUser?.role || '').toLowerCase();
  const isSalesUser = userDept === 'sales' || userRole === 'sales' || userRole === 'sales_executive' || userRole === 'sales_admin' || userRole === 'bde' || userRole === 'bda';
  const isSalesOnly = isSalesUser && !isSuperAdminOrOwner && userDept !== 'accounts';
  const isDraftOwner = (qObj: any, reqObj: any) => {
    if (!currentUser) return true;
    if (isSuperAdminOrOwner || userDept === 'accounts') return true;
    const cId = String(currentUser.id || '').toLowerCase();
    const cName = String(currentUser.name || currentUser.username || '').toLowerCase();

    const reqUser = String(reqObj?.requestedById || reqObj?.createdById || reqObj?.userId || '').toLowerCase();
    const reqName = String(reqObj?.requestedByName || reqObj?.requestedBy || reqObj?.createdBy || '').toLowerCase();
    const qUser = String(qObj?.created_by || qObj?.createdBy || qObj?.userId || qObj?.author_id || qObj?.sales_executive_id || qObj?.salesExecutiveId || '').toLowerCase();
    const qName = String(qObj?.salesExecutive || qObj?.sales_executive || '').toLowerCase();

    if (!reqUser && !reqName && !qUser && !qName) return true;

    return (
      (Boolean(cId) && (reqUser === cId || qUser === cId)) ||
      (Boolean(cName) && (reqName === cName || qName === cName))
    );
  };
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocData, setSelectedDocData] = useState<DocumentWorkspaceData | null>(null);
  const [showProformaForm, setShowProformaForm] = useState(false);
  const [sendEmailDoc, setSendEmailDoc] = useState<{ id: string; number: string; clientEmail?: string; clientName?: string } | null>(null);
  const [proformaQuotationId, setProformaQuotationId] = useState<string | undefined>(undefined);
  const [proformaQuotationNumber, setProformaQuotationNumber] = useState<string | undefined>(undefined);
  const [proformaMilestoneIndex, setProformaMilestoneIndex] = useState<number | undefined>(undefined);
  const [proformaMilestonePhaseLabel, setProformaMilestonePhaseLabel] = useState<string | undefined>(undefined);
  const [proformaFromDeal, setProformaFromDeal] = useState<any | null>(null);
  const [proformaFromLead, setProformaFromLead] = useState<any | null>(null);
  const [editingQuotation, setEditingQuotation] = useState<{ id: string; data: any; inPlace?: boolean } | null>(null);
  const [pendingDraftDelete, setPendingDraftDelete] = useState<{ id: string; number: string } | null>(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  // Resubmission form state
  const [resubmissionFormData, setResubmissionFormData] = useState<{
    isOpen: boolean;
    docData: DocumentWorkspaceData | null;
    isProforma: boolean;
    isRevision?: boolean;
  } | null>(null);

  // Open the edit form in resubmission mode
  const handleOpenResubmissionForm = (docData: DocumentWorkspaceData) => {
    const isProforma = docData.requestType === 'proforma' || docData.status === 'proforma_rejected';
    
    // Only a proforma goes through resubmission now; quotations need no approval.
    if (!isProforma) return;
    setResubmissionFormData({
      isOpen: true,
      docData,
      isProforma: true,
    });
    setDrawerOpen(false);
    smoothScrollToTop();
  };

  // A quotation that has not been emailed yet is edited in place (same quotation, no new
  // revision). Once it was emailed to the client, "Revise" creates a new quotation with a new
  // number based on it.
  const startQuotationRevision = (docData: DocumentWorkspaceData) => {
    setDrawerOpen(false);
    if (hasQuotationBeenEmailed(docData as any)) {
      const number = (docData as any).number || (docData as any).documentNumber || '';
      setEditingQuotation({
        id: '',
        data: { ...docData, revisedFrom: { id: docData.id, number } },
      });
    } else {
      setEditingQuotation({ id: docData.id, data: docData, inPlace: true });
    }
    smoothScrollToTop();
  };

  // Open the edit form in revision mode (for "Revise Quotation" or "Revise Proforma" button)
  const handleOpenRevisionForm = (docData: DocumentWorkspaceData) => {
    const isProforma = docData.requestType === 'proforma' || docData.documentType === 'proforma' || docData.status?.startsWith('proforma');
    if (!isProforma) {
      startQuotationRevision(docData);
      return;
    }
    setResubmissionFormData({
      isOpen: true,
      docData,
      isProforma: Boolean(isProforma),
      isRevision: true,
    });
    setDrawerOpen(false);
    smoothScrollToTop();
  };

  // Smooth scroll to top when switching tabs or toggling forms
  useEffect(() => {
    smoothScrollToTop();
  }, [activeTab, showCreateForm, editingQuotation, showProformaForm, resubmissionFormData]);

  // Auto-open proforma form when navigated from Invoice Requests or other pages
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const state = location.state as any;

    const shouldOpenProforma = state?.openProforma || searchParams.get('newProforma') === 'true' || searchParams.get('openProforma') === 'true' || searchParams.get('new') === 'proforma' || searchParams.get('requestProforma') === 'true';

    if (shouldOpenProforma) {
      const qId = state?.initialQuotationId || state?.quotationId || searchParams.get('quotationId') || undefined;
      const qNo = state?.initialQuotationNumber || state?.quotationNumber || searchParams.get('quotationNumber') || undefined;
      const rawMIdx = state?.initialMilestoneIndex ?? searchParams.get('milestoneIndex');
      const mIdx = rawMIdx !== undefined && rawMIdx !== null && !isNaN(Number(rawMIdx)) ? Number(rawMIdx) : undefined;
      const mPhase = state?.initialMilestonePhaseLabel || searchParams.get('milestonePhaseLabel') || undefined;

      if (qId) setProformaQuotationId(qId);
      if (qNo) setProformaQuotationNumber(qNo);
      if (typeof mIdx === 'number') setProformaMilestoneIndex(mIdx);
      if (mPhase) setProformaMilestonePhaseLabel(mPhase);
      if (state?.fromDeal) setProformaFromDeal(state.fromDeal);
      if (state?.fromLead) setProformaFromLead(state.fromLead);

      const dealId = state?.fromDeal?.dealId || searchParams.get('dealId');
      if (dealId && !state?.fromDeal) {
        api.get(`/api/deals/${dealId}`).then((res) => {
          const d = res.data?.deal || res.data?.data || res.data;
          if (d) setProformaFromDeal(d);
        }).catch(() => {});
      }

      setShowProformaForm(true);

      // Clean up the URL search params & state so refreshing doesn't re-trigger
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state, location.search]);

  // Auto-open form when navigated from Deals or Leads page or URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const state = location.state as {
      fromDeal?: { dealId: string; title: string; clientName: string; phone?: string; email?: string; value?: number; businessName?: string; company?: string; contactPerson?: string; contactDesignation?: string; description?: string; services?: string[] };
      fromLead?: { leadId: string; title: string; clientName: string; phone?: string; email?: string; value?: number; businessName?: string; company?: string; contactPerson?: string; description?: string; services?: string[] };
    } | null;
    const isNewQuotation = searchParams.get('new') === 'quotation' || searchParams.get('new') === 'true' || searchParams.get('create') === 'true' || Boolean(searchParams.get('dealId')) || Boolean(searchParams.get('leadId'));

    if (state?.fromDeal || state?.fromLead || isNewQuotation) {
      // Wait for master data (rates, HSN/SAC, units), then apply once for this navigation.
      if (masterLoading || dealPrefillKeyRef.current === location.key) return;
      dealPrefillKeyRef.current = location.key;
      const dealId = state?.fromDeal?.dealId || searchParams.get('dealId') || '';
      const leadId = state?.fromLead?.leadId || searchParams.get('leadId') || '';

      const applyDealToQuotation = (dealObj: any) => {
        const clientName = dealObj?.clientName || dealObj?.client || dealObj?.company || dealObj?.business_name || dealObj?.businessName || dealObj?.name || searchParams.get('clientName') || searchParams.get('company') || searchParams.get('customerName') || dealObj?.title || searchParams.get('title') || '';
        const contactPerson = dealObj?.contactPerson || dealObj?.contact_name || dealObj?.name || searchParams.get('contactPerson') || '';
        // A deal stores the designation; a lead keeps it in its Role field.
        const contactDesignation = dealObj?.contactDesignation || (leadId ? dealObj?.role : '') || searchParams.get('contactDesignation') || '';
        const email = dealObj?.contactEmail || dealObj?.email || searchParams.get('email') || searchParams.get('clientEmail') || '';
        const phone = dealObj?.contactPhone || dealObj?.phone || searchParams.get('phone') || searchParams.get('clientPhone') || '';
        const rawVal = dealObj?.value ?? dealObj?.amount ?? dealObj?.estimated_value ?? searchParams.get('value');
        const value = rawVal !== undefined && rawVal !== null && !isNaN(Number(rawVal)) ? Number(rawVal) : 0;
        const title = dealObj?.title || dealObj?.name || searchParams.get('title') || '';
        const description = dealObj?.description || dealObj?.notes || searchParams.get('description') || '';

        let address = dealObj?.clientAddress || dealObj?.address || dealObj?.billingAddress || dealObj?.billing_address || searchParams.get('clientAddress') || searchParams.get('address') || '';
        let gstin = dealObj?.clientGstin || dealObj?.gstin || searchParams.get('clientGstin') || searchParams.get('gstin') || '';

        if (!address && (dealObj?.notes || description)) {
          const noteText = String(dealObj?.notes || description);
          const am = noteText.match(/(?:Address|Billing Address|Location):\s*([^,\n\)]+)/i);
          if (am) address = am[1].trim();
        }
        if (!gstin && (dealObj?.notes || description)) {
          const noteText = String(dealObj?.notes || description);
          const gm = noteText.match(/(?:GSTIN|GST|Tax ID):\s*([A-Z0-9]{15})/i);
          if (gm) gstin = gm[1].trim();
        }

        const rawServices: string[] = Array.isArray(dealObj?.services) && dealObj.services.length > 0
          ? dealObj.services
          : (dealObj?.service_required ? dealObj.service_required.split(',').map((s: string) => s.trim()).filter(Boolean)
          : (dealObj?.serviceRequired ? dealObj.serviceRequired.split(',').map((s: string) => s.trim()).filter(Boolean)
          : (searchParams.get('services') ? searchParams.get('services')!.split(',').map((s: string) => s.trim()).filter(Boolean) : [])));

        let initialLines: any[] | undefined = undefined;
        if (Array.isArray(rawServices) && rawServices.length > 0) {
          initialLines = rawServices.map((srvName, idx) => {
            // Rate, HSN/SAC, unit and tax come from master data; the rep enters the negotiated price on the quotation.
            const f = serviceLineFields(srvName, masterData?.productServices ?? [], masterData?.hsnSacCodes ?? []);

            return {
              key: `srv-${idx + 1}-${Math.random().toString(36).slice(2, 7)}`,
              name: f.name,
              description: f.description,
              hsnSac: f.hsnSac,
              quantity: 1,
              rate: f.rate,
              discount: 0,
              discountType: 'percent',
              gstRate: f.gstRate,
              cgstRate: f.gstRate / 2,
              sgstRate: f.gstRate / 2,
              igstRate: 0,
              unit: f.unit,
              catalogItemId: null,
              image: null,
              missingFromMaster: f.missingFromMaster,
            };
          });
        } else if (value > 0) {
          initialLines = [{
            key: 'item-1',
            name: title || 'Commercial Services',
            description: description || title || 'Service Scope & Commercials',
            hsnSac: '998314',
            quantity: 1,
            rate: value,
            discount: 0,
            discountType: 'percent',
            gstRate: 18,
            cgstRate: 9,
            sgstRate: 9,
            igstRate: 0,
            unit: 'project',
            catalogItemId: null,
            image: null,
          }];
        }

        setEditingQuotation({
          id: '',
          data: {
            clientId: dealId || leadId || '',
            sourceDealId: dealId || undefined,
            dealId: dealId || undefined,
            linkedCrmDealId: dealId || undefined,
            dealTitle: title,
            dealStage: dealObj?.stage || 'Closed Won',
            sourceLeadId: leadId || undefined,
            clientName: clientName,
            clientEmail: email,
            clientPhone: phone,
            clientAddress: address,
            clientGstin: gstin,
            placeOfSupply: dealObj?.clientState || searchParams.get('clientState') || '',
            contactPerson: contactPerson,
            contactDesignation: contactDesignation,
            services: rawServices,
            subject: title ? `Proposal for ${title}` : (clientName ? `Proposal for ${clientName}` : undefined),
            introText: description ? `Proposal for ${clientName || title}. ${description}` : (clientName ? `Proposal for ${clientName} covering project deliverables, scope and commercials.` : undefined),
            lines: initialLines,
          }
        });
        setShowCreateForm(true);
        window.history.replaceState({}, document.title, location.pathname);
      };

      if (dealId && (!state?.fromDeal || !state?.fromDeal?.services?.length || !state?.fromDeal?.value || !state?.fromDeal?.contactDesignation)) {
        // Fetch fresh deal data from API to ensure complete services and values are loaded
        api.get(`/deals/${dealId}`)
          .then(res => {
            const d = res.data?.deal || res.data?.data || res.data;
            applyDealToQuotation({ ...state?.fromDeal, ...d });
          })
          .catch(() => {
            applyDealToQuotation(state?.fromDeal || {});
          });
      } else if (leadId && (!state?.fromLead && (!state?.fromDeal || !state?.fromDeal?.services?.length || !state?.fromDeal?.value))) {
        // Fetch fresh lead data from API to ensure complete services and values are loaded
        api.get(`/leads/${leadId}`)
          .then(res => {
            const l = res.data?.lead || res.data?.data || res.data;
            applyDealToQuotation({ ...state?.fromLead, ...state?.fromDeal, ...l });
          })
          .catch(() => {
            applyDealToQuotation(state?.fromLead || state?.fromDeal || {});
          });
      } else {
        applyDealToQuotation(state?.fromDeal || state?.fromLead || {});
      }
    }
  }, [location.state, location.search, location.key, masterLoading]);

  // Auto-open revision form when navigated with ?revise=<docId>
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const reviseDocId = searchParams.get('revise');
    if (reviseDocId) {
      (async () => {
        try {
          const { data: freshDoc } = await api.get(`/invoice-requests/${reviseDocId}`);
          const latestDoc = freshDoc?.request || freshDoc?.doc || freshDoc?.data || freshDoc;
          if (latestDoc) {
            const docData: any = {
              id: latestDoc._id || latestDoc.id,
              ...latestDoc,
              nextRevisionLabel: latestDoc.currentRevisionNumber
                ? `R${latestDoc.currentRevisionNumber + 1}`
                : (latestDoc.documentRevisions?.length ? `R${latestDoc.documentRevisions.length + 1}` : 'R2'),
            };
            handleOpenRevisionForm(docData as any);
          }
        } catch (err) {
          console.error('Failed to load doc for revise param', err);
        }
      })();
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search]);

  const { data: reqsData, isLoading: reqsLoading, error: reqsError } = useInvoiceRequests();
  const requests = useMemo(() => {
    if (!reqsData) return [];
    if (Array.isArray(reqsData)) return reqsData;
    if (Array.isArray((reqsData as any).requests)) return (reqsData as any).requests;
    if (Array.isArray((reqsData as any).items)) return (reqsData as any).items;
    return [];
  }, [reqsData]);

  // Also fetch quotation-specific invoice requests directly as fallback
  const { data: qtReqsData } = useQuery({
    queryKey: ['invoice-requests', 'quotations-direct'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/invoice-requests?requestType=quotation');
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.requests)) return data.requests;
        if (Array.isArray(data?.items)) return data.items;
        return [];
      } catch {
        return [];
      }
    },
    refetchInterval: 15000,
  });
  const quotationRequests = useMemo(() => (Array.isArray(qtReqsData) ? qtReqsData : []), [qtReqsData]);

  // The quotations list is built from invoice requests (requestType 'quotation').
  const allQuotations = useMemo<Quotation[]>(() => {
    const safeRequests = Array.isArray(requests) ? requests : [];
    const safeQtReqs = Array.isArray(quotationRequests) ? quotationRequests : [];

    const allReqs = [...safeRequests];
    for (const qr of safeQtReqs) {
      if (qr && qr.id && !allReqs.find(r => r && r.id === qr.id)) allReqs.push(qr);
    }
    const directIRQuotations = allReqs
      .filter(r => r && r.requestType === 'quotation' && (!r.number || !/^(?:PI|TI|INV|SO)/i.test(r.number)))
      .map(r => ({
        _id: r.id || r._id || Math.random().toString(),
        documentNumber: r.number || (r.id || r._id ? `QT-${(r.id || r._id).slice(-6)}` : 'QT-NEW'),
        date: r.requestedAt || new Date().toISOString(),
        documentType: 'quotation',
        status: r.status || 'draft',
        customerName: r.clientName || '',
        customerEmail: '',
        customerPhone: '',
        lines: (Array.isArray(r.items) ? r.items : []).map((item: any) => ({
          productId: item.catalogItemId || null,
          description: item.name || item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.rate || 0,
          discount: item.discountPercent || 0,
          discountType: 'percent' as const,
          cgstRate: (item.taxRate || 0) / 2,
          sgstRate: (item.taxRate || 0) / 2,
          igstRate: 0,
        })),
        subTotal: r.subtotal || 0,
        taxTotal: r.taxTotal || 0,
        grandTotal: r.total || 0,
        notes: r.notes || '',
        createdBy: r.preparedBy || r.requestedByName || '',
        preparedBy: r.preparedBy || r.requestedByName || '',
        _isDirectIR: true,
      } as any));
    return directIRQuotations.sort((a: Quotation, b: Quotation) => {
      const dateA = new Date(a?.date || a?.createdAt || 0).getTime();
      const dateB = new Date(b?.date || b?.createdAt || 0).getTime();
      return dateB - dateA; // newest first
    });
  }, [requests, quotationRequests]);

  // Distinct sales reps who prepared at least one listed quotation.
  const preparedByOptions = useMemo(
    () => Array.from(new Set(allQuotations.map((q: any) => q.preparedBy || q.createdBy || '').filter(Boolean))).sort() as string[],
    [allQuotations]
  );

  const visibleQuotations = useMemo(
    () => (preparedByFilter ? allQuotations.filter((q: any) => (q.preparedBy || q.createdBy || '') === preparedByFilter) : allQuotations),
    [allQuotations, preparedByFilter]
  );

  const stats = useMemo(() => {
    const total = allQuotations.length;
    const totalValue = allQuotations.reduce((sum, q) => sum + (Number(q.grandTotal) || 0), 0);
    const openQuotes = allQuotations.filter((q) => ['draft', 'approved'].includes(q.status || '')).length;
    return { total, totalValue, openQuotes };
  }, [allQuotations]);

  const handleOpenDrawer = (q?: Quotation | null, req?: any, docType: 'quotation' | 'proforma' = 'quotation') => {
    try {
      const qDoc = q || (req?._quotDoc ? req._quotDoc : null);
      let htmlToShow = '';

      if (docType === 'quotation') {
        const lastRevision = req?.documentRevisions?.[req.documentRevisions.length - 1];
        // Trust the stored snapshot: this is the artifact that was submitted and approved.
        // Regenerate only when the document has no stored HTML at all (legacy docs).
        htmlToShow = req?.currentHtmlBody || (qDoc as any)?.currentHtmlBody || (qDoc as any)?.htmlBody || lastRevision?.htmlBody || '';
      } else {
        // proforma — stored snapshot first (proformaHtmlBody, then the generic bodies)
        const revisions = req?.documentRevisions || [];
        const proformaRevision = revisions.slice().reverse().find((r: any) => r.status === 'proforma_approved' || r.note?.toLowerCase().includes('proforma'));
        htmlToShow = req?.proformaHtmlBody || (req as any)?.currentHtmlBody || (req as any)?.htmlBody || proformaRevision?.htmlBody || '';
        // If the proforma charges GST but the stored HTML was baked without tax columns
        // (e.g. auto-converted from a GST-off quotation), discard it so the preview
        // regenerates with tax in the grand total and in each milestone stage split.
        const pfWantsGst = ((req as any)?.gstEnabled ?? true) !== false && !(req as any)?.reverseCharge;
        if (htmlToShow && pfWantsGst && !htmlToShow.includes('data-tax')) htmlToShow = '';
      }

      const isPlaceholderItem = (it: any) => {
        if (!it) return false;
        const str = String(it.name || it.description || '').toLowerCase();
        return str.includes('full 100% payment on delivery') || str.includes('payment on delivery / order for quotation');
      };
      const qDocItems = (qDoc as any)?.lines || (qDoc as any)?.items || (qDoc as any)?.template_data?.items || (qDoc as any)?.template_data?.lines || [];
      const reqItems = req?.items || (req as any)?.lines || (req as any)?.template_data?.items || (req as any)?.template_data?.lines || [];
      const hasRealReqItems = Array.isArray(reqItems) && reqItems.length > 0 && !reqItems.every(isPlaceholderItem);
      const rawItems = hasRealReqItems ? reqItems : (qDocItems.length > 0 ? qDocItems : reqItems);
      const filteredRawItems = Array.isArray(rawItems) ? rawItems.filter(it => !isPlaceholderItem(it)) : [];
      const finalItemsList = filteredRawItems.length > 0 ? filteredRawItems : (Array.isArray(rawItems) ? rawItems : []);

      const items = finalItemsList.length > 0 ? finalItemsList.map((it: any) => {
        const name = it.name || it.description || 'Deliverable / service';
        // Keep the per-line note visible: it only renders when carried inside subDescription.
        const subDesc = printedLineNote(it);
        return {
          name,
          description: name,
          subDescription: subDesc,
          notes: it.notes || '',
          hsn: it.hsn || it.hsnSac || '998314',
          quantity: Number(it.quantity ?? it.qty ?? 1),
          unit: it.unit || 'Nos',
          unitPrice: Number(it.unitPrice ?? it.rate ?? 0),
          discountPercent: Number(it.discountPercent ?? (it.discount !== undefined ? it.discount : (it.disc ?? 0))),
          gstRate: Number(it.gstRate ?? (it.taxRate !== undefined ? it.taxRate : (((it.cgstRate || 0) + (it.sgstRate || 0) + (it.igstRate || 0)) || 18))),
        };
      }) : undefined;

      const rawMilestones: any[] =
        (qDoc as any)?.paymentSchedule?.milestones
        || (qDoc as any)?.template_data?.milestones
        || (qDoc as any)?.milestones
        || req?.paymentSchedule?.milestones
        || req?.template_data?.milestones
        || (req as any)?.milestones
        || [];
      // Normalize all field-name variants → { name, percent, due }
      const msList: Array<{ name: string; percent: number; due: string }> = Array.isArray(rawMilestones) ? rawMilestones.map((m: any) => ({
        name: m.name || m.label || `Stage ${m.milestoneNumber || ''}`,
        percent: Number(m.percent ?? m.pct ?? m.percentage ?? 0),
        due: m.due || (m.daysFromApproval ? `T + ${m.daysFromApproval} days` : m.dueDate || '—'),
        // Preserve live payment/invoice status fields for richer template display
        status: m.status,
        invoiceNumber: m.invoiceNumber,
        amountReceived: m.amountReceived,
        amountInvoiced: m.amountInvoiced,
        amountDue: m.amountDue,
        netAmount: m.netAmount,
        taxAmount: m.taxAmount,
        cgstAmount: m.cgstAmount,
        sgstAmount: m.sgstAmount,
      })) : [];

      const explicitPaymentType = (qDoc as any)?.paymentType || (req as any)?.paymentType || (qDoc as any)?.template_data?.paymentType || req?.template_data?.paymentType || (req as any)?.paymentSchedule?.paymentType || (req as any)?.paymentSchedule?.scheduleMethod;
      const isExplicitFullPayment = explicitPaymentType === 'full_payment' || explicitPaymentType === 'full';
      const effectivePaymentType = isExplicitFullPayment ? 'full_payment' : (msList.length > 0 ? 'milestone_based' : 'full_payment');
      const effectiveMilestones = isExplicitFullPayment ? undefined : (msList.length > 0 ? msList : undefined);

      // Trust the stored snapshot — the payment type / milestone-section mismatch check used to
      // discard approved HTML and regenerate it with different inputs. Only regenerate when the
      // document has no stored HTML at all.
      const needsRegenerate = !htmlToShow;

      // Regenerate if no html exists or if paymentType changed vs cached HTML
      if (needsRegenerate) {
        const safeFormatDate = (raw: any): string | undefined => {
          if (!raw) return undefined;
          try {
            const dt = new Date(raw);
            if (isNaN(dt.getTime())) return typeof raw === 'string' ? raw : undefined;
            return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
          } catch {
            return typeof raw === 'string' ? raw : undefined;
          }
        };

        const docDateStr = safeFormatDate(qDoc?.date) || safeFormatDate(req?.requestedAt) || safeFormatDate(req?.date);
        const validUntilStr = safeFormatDate((qDoc as any)?.validTillDate) || safeFormatDate((qDoc as any)?.validUntil) || safeFormatDate(req?.validUntil);

        const coLogo = (qDoc as any)?.businessLogo || (qDoc as any)?.companyLogo || (req as any)?.template_data?.businessLogo || (req as any)?.template_data?.companyLogo || (req as any)?.businessLogo || (req as any)?.companyLogo || '';
        const coSig = (qDoc as any)?.signatureUrl || (qDoc as any)?.template_data?.signatureUrl || (req as any)?.template_data?.signatureUrl || (req as any)?.signatureUrl || '';
        if (docType === 'proforma') {
          const rawMIdx = (req as any)?.milestoneIndex ?? (req as any)?.template_data?.milestoneIndex ?? (qDoc as any)?.milestoneIndex ?? inferMilestoneIndex(req) ?? inferMilestoneIndex(qDoc);
          const activeMilestoneIndex = (rawMIdx !== undefined && rawMIdx !== null && !isNaN(Number(rawMIdx))) ? Number(rawMIdx) : null;
          const activeMilestone = (activeMilestoneIndex !== null && Array.isArray(msList) && msList.length > activeMilestoneIndex) ? msList[activeMilestoneIndex] : null;

          const stagePct = activeMilestone
            ? Number(activeMilestone.percent ?? (activeMilestone as any).pct ?? (activeMilestone as any).percentage ?? 0)
            : 0;
          const stageName = activeMilestone
            ? (activeMilestone.name || (activeMilestone as any).label || `Stage ${(activeMilestoneIndex ?? 0) + 1}`)
            : undefined;

          // The proforma record's own totals are stored post-discount (both the server and the
          // proforma form store header-discount-applied values), so they are used as-is —
          // re-applying effHDisc here would double-discount the milestone table base.
          const pfRec = (req as any)?.requestType === 'proforma' ? (req as any) : null;
          const rawQuotSubtotal = Number(pfRec?.subtotal || qDoc?.subtotal || req?.subtotal || 0);
          const rawQuotTax = Number(pfRec?.taxTotal ?? (qDoc?.taxTotal || req?.taxTotal || 0));
          const rawQuotGrand = Number(pfRec?.total || qDoc?.total || req?.total || 0);
          const effHDisc = Number((qDoc as any)?.headerDiscount ?? (qDoc as any)?.template_data?.headerDiscount ?? req?.headerDiscount ?? (req as any)?.template_data?.headerDiscount ?? 0);
          const effHDiscType = String((qDoc as any)?.headerDiscountType ?? (qDoc as any)?.template_data?.headerDiscountType ?? req?.headerDiscountType ?? (req as any)?.template_data?.headerDiscountType ?? 'percent');
          const discQuotSubtotal = rawQuotSubtotal;
          const discQuotTax = rawQuotTax;
          const discQuotGrand = rawQuotGrand;

          let generatedHtml = generateFullProformaInvoiceHtml({
            documentNumber: req?.proformaNumber || (qDoc?.documentNumber ? qDoc.documentNumber.replace('QTN', 'PI') : 'PI/2026-27/0001'),
            date: docDateStr,
            customerName: qDoc?.customerName || req?.clientName || '',
            customerEmail: qDoc?.customerEmail || req?.clientEmail,
            customerContact: qDoc?.customerPhone || req?.clientPhone,
            customerAddress: (qDoc as any)?.customerAddress || req?.clientAddress,
            customerGstin: (qDoc as any)?.customerGstin || req?.clientGstin,
            items,
            notes: (qDoc as any)?.notes || (qDoc as any)?.template_data?.notes || req?.notes || (req as any)?.template_data?.notes,
            terms: (qDoc as any)?.terms || (qDoc as any)?.template_data?.terms || req?.terms || (req as any)?.template_data?.terms,
            termsList: (qDoc as any)?.termsList || (qDoc as any)?.template_data?.termsList || (req as any)?.termsList || (req as any)?.template_data?.termsList,
            assumptions: (qDoc as any)?.assumptions || (qDoc as any)?.template_data?.assumptions || req?.assumptions || (req as any)?.template_data?.assumptions,
            assumptionsList: (qDoc as any)?.assumptionsList || (qDoc as any)?.template_data?.assumptionsList || (req as any)?.assumptionsList || (req as any)?.template_data?.assumptionsList,
            declaration: (qDoc as any)?.declaration || (qDoc as any)?.template_data?.declaration || req?.declaration || (req as any)?.template_data?.declaration,
            // stagePercent scales item rates so all computed totals (taxable, GST, grand) are milestone-scoped
            stagePercent: (stagePct > 0 && stagePct < 100) ? stagePct : undefined,
            milestoneIndex: activeMilestoneIndex ?? undefined,
            stageAmountsAreFinal: false,
            // GST comes from the proforma itself; without it the template charges GST by place of supply.
            gstEnabled: pfRec?.gstEnabled,
            gstMode: pfRec?.gstEnabled === false ? 'none' : (pfRec?.gstMode || undefined),
            customerState: (qDoc as any)?.placeOfSupply || req?.placeOfSupply || req?.clientState,
            companyGstin: (qDoc as any)?.businessGstin || req?.businessGstin,
            headerDiscount: effHDisc,
            headerDiscountType: effHDiscType as any,
            additionalCharges: Number((qDoc as any)?.additionalCharges ?? req?.additionalCharges ?? 0) || undefined,
            additionalChargesLabel: (qDoc as any)?.additionalChargesLabel || req?.additionalChargesLabel || undefined,
            discount: (qDoc as any)?.discount ?? (qDoc as any)?.template_data?.discount ?? req?.discount ?? (req as any)?.template_data?.discount,
            // Explicit full-contract totals so the milestone schedule table splits correctly per stage
            contractTaxable: (effHDisc > 0 ? discQuotSubtotal : rawQuotSubtotal) || undefined,
            contractGrand:   (effHDisc > 0 ? discQuotGrand : rawQuotGrand) || undefined,
            contractTax:     (effHDisc > 0 ? discQuotTax : rawQuotTax) || undefined,
            paymentType: effectivePaymentType,
            milestones: effectiveMilestones,
            bankAccName: (qDoc as any)?.bankAccName || req?.bankAccName,
            bankAccNo: (qDoc as any)?.bankAccNo || req?.bankAccNo,
            bankName: (qDoc as any)?.bankName || req?.bankName,
            bankBranch: (qDoc as any)?.bankBranch || req?.bankBranch,
            bankIfsc: (qDoc as any)?.bankIfsc || req?.bankIfsc,
            companyState,
            bankUpi: (qDoc as any)?.bankUpi || req?.bankUpi,
            signatory: (qDoc as any)?.signatory || req?.signatory,
            signatoryRole: (qDoc as any)?.signatoryRole || req?.signatoryRole,
            signatureUrl: coSig,
            companyLogo: coLogo,
            availableProducts: productData || [],
            displayOptions: (req as any)?.displayOptions || (req as any)?.template_data?.displayOptions || (qDoc as any)?.displayOptions || (qDoc as any)?.template_data?.displayOptions || {},
          });
          // Annotate milestone subtitle under heading
          if (stageName && stagePct > 0) {
            generatedHtml = generatedHtml.replace(
              /(<h1[^>]*>Proforma Invoice<\/h1>)/i,
              `$1<p style="margin:2px 0 8px;font-size:12px;color:#555;font-style:italic;">Milestone: ${stageName} (${stagePct}% of contract)</p>`
            );
          }
          htmlToShow = generatedHtml;
        } else {
          let baseQuotNumber = (
            qDoc?.documentNumber ||
            req?.number ||
            req?.sourceQuotationNumber ||
            'QT/2026-27/0001'
          );
          htmlToShow = generateFullQuotationHtml({
            quotationNumber: baseQuotNumber,
            quotationDate: docDateStr,
            validUntil: validUntilStr,
            companyName: (qDoc as any)?.businessName || req?.businessName,
            companyTag: (qDoc as any)?.businessTag || req?.businessTag,
            companyAddress: (qDoc as any)?.businessAddress || req?.businessAddress,
            companyGstin: (qDoc as any)?.businessGstin || req?.businessGstin,
            companyPan: (qDoc as any)?.businessPan || req?.businessPan,
            companyPhone: (qDoc as any)?.businessPhone || req?.businessPhone,
            companyEmail: (qDoc as any)?.businessEmail || req?.businessEmail,
            companyWeb: (qDoc as any)?.businessWeb || req?.businessWeb,
            companyLogo: coLogo,
            customerName: qDoc?.customerName || req?.clientName || '',
            customerEmail: qDoc?.customerEmail || req?.clientEmail,
            customerPhone: qDoc?.customerPhone || req?.clientPhone,
            customerAddress: (qDoc as any)?.customerAddress || req?.clientAddress,
            customerGstin: (qDoc as any)?.customerGstin || req?.clientGstin,
            customerState: (qDoc as any)?.placeOfSupply || req?.placeOfSupply || req?.clientState,
            contactPerson: (qDoc as any)?.contactPerson || req?.contactPerson,
            contactDesignation: (qDoc as any)?.contactDesignation || req?.contactDesignation,
            paymentTerms: (qDoc as any)?.paymentTerms || req?.paymentTerms,
            paymentTermsDescription: (masterData?.paymentTerms ?? []).find((t: any) => t.name === ((qDoc as any)?.paymentTerms || req?.paymentTerms))?.description || '',
            preparedBy: (qDoc as any)?.preparedBy || req?.preparedBy || req?.requestedByName,
            subject: (qDoc as any)?.subject || req?.subject,
            introText: (qDoc as any)?.introText || req?.introText,
            items,
            notes: (qDoc as any)?.notes || (qDoc as any)?.template_data?.notes || req?.notes || (req as any)?.template_data?.notes,
            subtotal: (qDoc as any)?.subtotal || (qDoc as any)?.subTotal || req?.subtotal,
            taxTotal: (qDoc as any)?.taxTotal || req?.taxTotal,
            grandTotal: (qDoc as any)?.total || (qDoc as any)?.grandTotal || req?.total,
            total: (qDoc as any)?.total || (qDoc as any)?.grandTotal || req?.total,
            headerDiscount: (qDoc as any)?.headerDiscount ?? (qDoc as any)?.template_data?.headerDiscount ?? req?.headerDiscount ?? (req as any)?.template_data?.headerDiscount ?? 0,
            headerDiscountType: (qDoc as any)?.headerDiscountType ?? (qDoc as any)?.template_data?.headerDiscountType ?? req?.headerDiscountType ?? (req as any)?.template_data?.headerDiscountType ?? 'percent',
            additionalCharges: Number((qDoc as any)?.additionalCharges ?? req?.additionalCharges ?? 0) || undefined,
            additionalChargesLabel: (qDoc as any)?.additionalChargesLabel || req?.additionalChargesLabel || undefined,
            discount: (qDoc as any)?.discount ?? (qDoc as any)?.template_data?.discount ?? req?.discount ?? (req as any)?.template_data?.discount,
            paymentType: effectivePaymentType,
            milestones: effectiveMilestones,
            gstEnabled: (qDoc as any)?.gstEnabled ?? (qDoc as any)?.template_data?.gstEnabled ?? req?.gstEnabled ?? (req as any)?.template_data?.gstEnabled,
            gstMode: ((qDoc as any)?.gstEnabled === false || req?.gstEnabled === false)
              ? 'none'
              : (((qDoc as any)?.reverseCharge || (req as any)?.reverseCharge)
                ? 'none'
                : ((qDoc as any)?.gstMode || req?.gstMode || 'intra')),
            reverseCharge: ((qDoc as any)?.reverseCharge || (req as any)?.reverseCharge) ? 'Yes' : 'No',
            assumptions: (qDoc as any)?.assumptions || (qDoc as any)?.template_data?.assumptions || req?.assumptions || (req as any)?.template_data?.assumptions,
            assumptionsList: (qDoc as any)?.assumptionsList || (qDoc as any)?.template_data?.assumptionsList || (req as any)?.assumptionsList || (req as any)?.template_data?.assumptionsList,
            terms: (qDoc as any)?.terms || (qDoc as any)?.template_data?.terms || req?.terms || (req as any)?.template_data?.terms,
            termsList: (qDoc as any)?.termsList || (qDoc as any)?.template_data?.termsList || (req as any)?.termsList || (req as any)?.template_data?.termsList,
            declaration: (qDoc as any)?.declaration || (qDoc as any)?.template_data?.declaration || req?.declaration || (req as any)?.template_data?.declaration,
            bankAccName: (qDoc as any)?.bankAccName || req?.bankAccName,
            bankAccNo: (qDoc as any)?.bankAccNo || req?.bankAccNo,
            bankName: (qDoc as any)?.bankName || req?.bankName,
            bankBranch: (qDoc as any)?.bankBranch || req?.bankBranch,
            bankIfsc: (qDoc as any)?.bankIfsc || req?.bankIfsc,
            bankUpi: (qDoc as any)?.bankUpi || req?.bankUpi,
            companyState,
            signatory: (qDoc as any)?.signatory || req?.signatory,
            signatoryRole: (qDoc as any)?.signatoryRole || req?.signatoryRole,
            signatureUrl: coSig,
            footerLeft: (qDoc as any)?.footerLeft || req?.footerLeft,
            footerRight: (qDoc as any)?.footerRight || req?.footerRight,
            availableProducts: productData || [],
            displayOptions: (req as any)?.displayOptions || (req as any)?.template_data?.displayOptions || (qDoc as any)?.displayOptions || (qDoc as any)?.template_data?.displayOptions || {},
          });

          if (htmlToShow) {
            htmlToShow = htmlToShow.replace(/(<span[^>]*id=["']dno["'][^>]*>)\s*(?:TI|PI)[^<]*(<\/span>)/gi, `$1${baseQuotNumber}$2`);
            htmlToShow = htmlToShow.replace(/(Quotation\s+no\.?:?\s*<\/span>\s*<span[^>]*>)\s*(?:TI|PI)[^<]*(<\/span>)/gi, `$1${baseQuotNumber}$2`);
            htmlToShow = htmlToShow.replace(/(<title>Quotation\s+)(?:TI|PI)[^—<]*(—)/gi, `$1${baseQuotNumber} $2`);
          }
        }
      }

      let baseQuotNumber = (
        qDoc?.documentNumber ||
        req?.number ||
        req?.sourceQuotationNumber ||
        'QT/2026-27/0001'
      );

      setSelectedDocData({
        id: req ? (req.id || req._id) : (qDoc?._id || qDoc?.id || ''),
        number: docType === 'quotation' ? baseQuotNumber : (req ? req.number : (qDoc?.documentNumber || qDoc?.number)),
        documentNumber: docType === 'proforma'
          ? (req?.proformaNumber || (qDoc?.documentNumber ? qDoc.documentNumber.replace('QTN', 'PI') : 'PI/2026-27/0001'))
          : baseQuotNumber,
        clientName: qDoc?.customerName || req?.clientName,
        customerName: qDoc?.customerName || req?.clientName,
        clientEmail: qDoc?.customerEmail || req?.clientEmail,
        status: req ? req.status : (qDoc?.status || 'draft'),
        requestType: docType as any,
        documentType: docType as any,
        htmlBody: htmlToShow,
        currentHtmlBody: htmlToShow,
        subtotal: qDoc?.subTotal || req?.subtotal || 0,
        taxTotal: qDoc?.taxTotal || req?.taxTotal || 0,
        total: qDoc?.grandTotal || req?.total || 0,
        notes: qDoc?.notes || req?.notes || '',
        reviewNote: req?.reviewNote,
        items: (qDoc as any)?.lines || (qDoc as any)?.items || req?.items || items || [],
        history: req?.history || [],
        documentRevisions: req?.documentRevisions || [],
        // Quotations need no approval; only a proforma can be returned.
        canApproveNow: docType === 'quotation' ? false : req?.canApproveNow,
        canRejectNow: docType === 'quotation' ? false : req?.canRejectNow,
        canSubmit: req?.canSubmit,
        canResubmitProforma: req?.canResubmitProforma,
        // Resubmission fields
        rejectionReason: req?.reviewNote,
        quotationNumber: req?.number || baseQuotNumber,
        emailSentAt: req?.emailSentAt || (qDoc as any)?.emailSentAt,
        supersededById: req?.supersededById || (qDoc as any)?.supersededById,
        supersededByNumber: req?.supersededByNumber || (qDoc as any)?.supersededByNumber,
        proformaId: (req as any)?.proformaId || (qDoc as any)?.proformaId,
        proformaNumber: req?.proformaNumber,
        currentRevisionLabel: req?.currentRevisionLabel,
        nextRevisionLabel: req?.currentRevisionNumber
          ? `R${req.currentRevisionNumber + 1}`
          : (req?.documentRevisions?.length ? `R${req.documentRevisions.length + 1}` : 'R2'),
      });
      setDrawerOpen(true);
    } catch (err) {
      console.error('Error in handleOpenDrawer:', err);
      toast.error('Could not open document preview');
    }
  };

  const handleSaveDrawerHtml = async (id: string, html: string, note?: string, structuredData?: any) => {
    const matchedReq = requests.find((r: any) => r.id === id);
    const isProforma = selectedDocData?.requestType === 'proforma' || html.includes('data-doc="proforma"') || html.includes('Proforma Invoice');
    const payload: any = {
      htmlBody: html,
      currentHtmlBody: html,
      note,
      isRevision: true,
      ...(structuredData || {}),
    };
    if (isProforma) {
      payload.proformaHtmlBody = html;
    }
    if (matchedReq) {
      // Save revision snapshot with structured data
      try {
        await api.post(`/invoice-requests/${id}/revision`, payload, { skipErrorToast: true });
      } catch (e) {
        console.warn('Could not save revision snapshot:', e);
      }
    }
    // Update the live currentHtmlBody and structured fields in DB
    await api.put(`/invoice-requests/${id}`, payload, { skipErrorToast: true });
    // Refresh data so next open fetches updated HTML and structured state
    await queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
  };

  const handleDrawerResubmit = async (id: string, html: string, note?: string) => {
    const row = requests.find((x: any) => x.id === id);
    try {
      if (html) {
        await api.post(`/invoice-requests/${id}/revision`, { htmlBody: html, note }, { skipErrorToast: true });
      }
      if (row?.status === 'proforma_rejected') {
        await actions.resubmitProforma.mutateAsync({ id, note });
        toast.success('Proforma request resubmitted.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'resubmit this request'));
    }
  };



  const handleDrawerReject = async (id: string, note: string) => {
    const row = requests.find((x: any) => x.id === id);
    const stage: 'quotation' | 'proforma' = row?.status?.startsWith('proforma') ? 'proforma' : 'quotation';
    try {
      await actions.reject.mutateAsync({ id, stage, reason: note });
      toast.success(`${stage === 'proforma' ? 'Proforma' : 'Quotation'} returned for revision.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'return this request'));
    }
  };

  const { data: productData } = useProducts();
  // const { data: dealsData } = useDeals();
  const actions = useInvoiceRequestActions();

  const convert = async (q: any, matchedReq?: any) => {
    const docId = matchedReq ? matchedReq.id : q._id;
    try {
      await api.post(`/invoice-requests/${docId}/submit`, undefined, { skipErrorToast: true });
      toast.success('Quotation confirmed.');
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'submit this quotation'));
    }
  };

  const confirmDraftDelete = async () => {
    if (!pendingDraftDelete) return;
    setDeletingDraft(true);
    try {
      await actions.deleteDraft.mutateAsync({ id: pendingDraftDelete.id });
      toast.success(`Draft ${pendingDraftDelete.number} deleted.`);
      setPendingDraftDelete(null);
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'delete this draft'));
    } finally {
      setDeletingDraft(false);
    }
  };

  // Opened from a deal ("View Quotation" on the Deals pages): show that quotation once the list is in.
  const viewedQuotationRef = useRef<string | null>(null);
  useEffect(() => {
    const viewId = new URLSearchParams(location.search).get('view');
    if (!viewId || viewedQuotationRef.current === viewId || reqsLoading) return;
    const row = allQuotations.find((q) => String(q.id || q._id) === viewId);
    const req = requests.find((r: any) => String(r.id || r._id) === viewId);
    if (!row && !req) {
      toast.error('That quotation could not be found.');
      viewedQuotationRef.current = viewId;
      return;
    }
    viewedQuotationRef.current = viewId;
    handleOpenDrawer(row ?? null, req ?? row, 'quotation');
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.search, reqsLoading, allQuotations, requests]);

  if (reqsLoading) return <div className="p-6"><Spinner /></div>;
  if (reqsError) return <div className="p-6 text-red-600">Failed to load requests. Please try again.</div>;

  // Show the comprehensive quotation creation / editing form
  if (showCreateForm || editingQuotation) {
    return (
      <div className="py-6 px-4 sm:px-6 rounded-2xl bg-slate-100 dark:bg-slate-950/60">
        <QuotationCreateForm
          editId={editingQuotation?.id}
          editData={editingQuotation?.data}
          editInPlace={editingQuotation?.inPlace}
          revisedFrom={editingQuotation?.data?.revisedFrom}
          quotationNumber={editingQuotation?.data?.number || editingQuotation?.data?.documentNumber}
          onClose={() => {
            setShowCreateForm(false);
            setEditingQuotation(null);
            smoothScrollToTop();
          }}
          onCreated={() => {
            setShowCreateForm(false);
            setEditingQuotation(null);
            queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
            queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
            queryClient.invalidateQueries({ queryKey: ['deals'] });
            smoothScrollToTop();
          }}
        />
      </div>
    );
  }

  // ── PROFORMA RESUBMISSION / REVISION FORM (full-page) ──
  if (resubmissionFormData?.isOpen && resubmissionFormData.isProforma) {
    const rawMIdx = (resubmissionFormData.docData as any)?.milestoneIndex;
    const mIdx = rawMIdx !== undefined && rawMIdx !== null && !isNaN(Number(rawMIdx)) ? Number(rawMIdx) : undefined;
    const mPhase = (resubmissionFormData.docData as any)?.milestonePhaseLabel;
    const srcQId = (resubmissionFormData.docData as any)?.sourceQuotationId || (resubmissionFormData.docData as any)?.quotationId;
    const srcQNo = (resubmissionFormData.docData as any)?.sourceQuotationNumber || (resubmissionFormData.docData as any)?.quotationNumber;

    return (
      <div className="py-6 px-4 sm:px-6 rounded-2xl bg-slate-100 dark:bg-slate-950/60">
        <ProformaInvoiceGenerator
          editingDraft={resubmissionFormData.docData as any}
          isResubmission={!resubmissionFormData.isRevision}
          isRevision={resubmissionFormData.isRevision}
          rejectionReason={resubmissionFormData.docData?.rejectionReason || resubmissionFormData.docData?.reviewNote || ''}
          proformaNumber={resubmissionFormData.docData?.proformaNumber || resubmissionFormData.docData?.number || ''}
          currentRevisionLabel={resubmissionFormData.docData?.currentRevisionLabel || 'R1'}
          nextRevisionLabel={resubmissionFormData.docData?.nextRevisionLabel || 'R2'}
          documentId={resubmissionFormData.docData?.id || ''}
          initialMilestoneIndex={mIdx}
          initialMilestonePhaseLabel={mPhase}
          initialQuotationId={srcQId}
          initialQuotationNumber={srcQNo}
          onClose={() => {
            setResubmissionFormData(null);
            navigate('/quotations');
            smoothScrollToTop();
          }}
          onSuccess={() => {
            setResubmissionFormData(null);
            queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
            navigate('/quotations');
            smoothScrollToTop();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-0">
      <AccountingSubNav />
      <div className="space-y-5">
      {showProformaForm && (
        <ProformaInvoiceGenerator
          initialQuotationId={proformaQuotationId}
          initialQuotationNumber={proformaQuotationNumber}
          initialMilestoneIndex={proformaMilestoneIndex}
          initialMilestonePhaseLabel={proformaMilestonePhaseLabel}
          fromDeal={proformaFromDeal}
          fromLead={proformaFromLead}
          onClose={() => {
            setShowProformaForm(false);
            setProformaQuotationId(undefined);
            setProformaQuotationNumber(undefined);
            setProformaMilestoneIndex(undefined);
            setProformaMilestonePhaseLabel(undefined);
            setProformaFromDeal(null);
            setProformaFromLead(null);
            smoothScrollToTop();
          }}
          onSuccess={() => {
            setShowProformaForm(false);
            setProformaQuotationId(undefined);
            setProformaQuotationNumber(undefined);
            setProformaMilestoneIndex(undefined);
            setProformaMilestonePhaseLabel(undefined);
            setProformaFromDeal(null);
            setProformaFromLead(null);
            queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
            smoothScrollToTop();
          }}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 hover:shadow-md transition-shadow"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">
              {isSalesOnly ? 'Quotations' : 'Quotations & Proforma Workflow'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isSalesOnly
                ? 'Create, manage, and track quotations.'
                : 'Quotations are active as soon as they are created. Raise a proforma from one, then the tax invoice.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isSalesOnly && (
            <Button
              variant={activeTab === 'lifecycle' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('lifecycle')}
            >
              <FileText className="h-4 w-4 mr-1.5" /> Document Lifecycle
            </Button>
          )}
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> New quotation
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Total Quotations</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{stats.total}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">All quotations</span>
          </div>
          <div className="rounded-full bg-primary/10 p-3.5 text-primary">
            <FileText className="h-6 w-6" />
          </div>
        </div>

        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Quotation Value</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{formatINR(stats.totalValue)}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">Across all quotations</span>
          </div>
          <div className="rounded-full bg-cyan-500/10 p-3.5 text-cyan-600">
            <Wallet className="h-6 w-6" />
          </div>
        </div>

        <div className={cn(cardClassName(), 'p-5 flex items-center justify-between')}>
          <div>
            <span className="text-body-sm font-semibold text-on-surface-variant">Open</span>
            <h3 className="text-display-sm font-bold text-on-surface mt-1">{stats.openQuotes}</h3>
            <span className="text-body-sm text-on-surface-variant mt-1 block">Draft or awaiting response</span>
          </div>
          <div className="rounded-full bg-amber-500/10 p-3.5 text-amber-600">
            <Clock className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Main Quotations List */}
      {activeTab === 'quotations' && (
        <>
          {preparedByOptions.length > 0 && (
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="text-body-sm text-on-surface-variant">{visibleQuotations.length} quotation{visibleQuotations.length === 1 ? '' : 's'}</span>
              <div className="relative w-56">
                <select
                  value={preparedByFilter}
                  onChange={(e) => setPreparedByFilter(e.target.value)}
                  className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-surface-container-low border border-outline-variant/40 rounded-lg text-on-surface focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer appearance-none"
                >
                  <option value="">All Prepared By</option>
                  {preparedByOptions.map((name) => (
                    <option key={name} value={name}>Prepared by {name}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-on-surface-variant">
                  <ChevronDown className="h-3.5 w-3.5" />
                </div>
              </div>
            </div>
          )}
          {visibleQuotations.length === 0 ? (
            <EmptyState
              title={preparedByFilter ? `No quotations prepared by ${preparedByFilter}` : 'No quotations created yet'}
              description="Write a quote for your services, then send a request to Accounts once the client accepts."
            />
          ) : (
            <div className="space-y-4">
              {visibleQuotations.map((q) => {
            const qIdStr = String(q.id || q._id || '').trim();
            const qNumStr = String(q.documentNumber || q.number || q.quotationNumber || '').trim();

            const quotCandidates = requests.filter((r: any) => {
              const rId = String(r.id || r._id || '').trim();
              const rSrcId = String(r.sourceQuotationId || '').trim();
              const rNum = String(r.number || r.documentNumber || '').trim();
              const rSrcNum = String(r.sourceQuotationNumber || '').trim();

              return (
                (qIdStr && (rId === qIdStr || rSrcId === qIdStr)) ||
                (qNumStr && (rNum === qNumStr || rSrcNum === qNumStr))
              );
            });

            // Match specifically the quotation request (requestType === 'quotation' or number starting with QT)
            const matchedQuotReq = quotCandidates.find((r: any) => r.requestType === 'quotation' || (r.number && /^QT/i.test(r.number)))
              || ((q as any)._isDirectIR ? q : null);

            // Downstream proforma or invoice requests in the same chain
            const matchedProformaReq = quotCandidates.find((r: any) => r.requestType === 'proforma' || (r.number && /^PI/i.test(r.number)));
            const matchedInvoiceReq = quotCandidates.find((r: any) => r.requestType === 'invoice' || (r.number && /^(?:TI|INV)/i.test(r.number)));

            // General matched request: prefer quotation request so quotation metadata drives the card
            const matchedReq = matchedQuotReq || quotCandidates[0] || ((q as any)._isDirectIR ? q : null);

            // Strictly compute the QT quotation number:
            let displayQuotationNumber =
              q.documentNumber ||
              matchedQuotReq?.number ||
              matchedReq?.sourceQuotationNumber ||
              matchedReq?.number ||
              `QT-${(q.documentNumber || q.id || q._id || '').replace(/^(?:PI|TI|INV|SO)[-_]?/i, '').slice(-6)}`;

            const reqStatus = matchedReq?.status || q.status;
            
            // A quotation needs no approval: it is active as soon as it is created (anything but a draft).
            const isDraft = (!matchedReq && q.status === 'draft') || Boolean(matchedReq && matchedReq.status === 'draft');
            const isIssued = !isDraft;
            const hasBeenEmailed = hasQuotationBeenEmailed(matchedQuotReq || matchedReq);
            // The request that carries the quotation itself (a merged legacy row can be the proforma).
            const emailTarget: any = matchedQuotReq || (matchedReq?.requestType === 'quotation' ? matchedReq : null);

            const hasProforma = Boolean(
              matchedProformaReq ||
              hasRealProformaNumber(matchedReq || {}) || matchedReq?.proformaId || matchedReq?.requestType === 'proforma' ||
              (matchedReq as any)?._proformaDoc || hasRealProformaNumber(q as any) ||
              reqStatus === 'proforma_approved' || reqStatus === 'sent'
            );

            const hasTaxInvoice = Boolean(
              matchedInvoiceReq ||
              matchedReq?.invoiceNumber ||
              matchedReq?.invoiceId ||
              ['sent', 'paid', 'partially_paid', 'overdue', 'invoice_generated'].includes(reqStatus || '') ||
              (matchedReq as any)?._invoiceDoc ||
              (q as any)?.invoiceNumber ||
              (q as any)?.invoiceId
            );

            const supersededDoc = [matchedQuotReq, matchedReq, q].find((d: any) => isQuotationSuperseded(d)) as any;
            const isSuperseded = Boolean(supersededDoc);

            const displayClientName = matchedQuotReq?.clientName || q.customerName || matchedReq?.clientName || 'No client';
            const displayLinesCount = (matchedQuotReq?.items || q.lines || matchedReq?.items)?.length || 0;
            const displayGrandTotal = matchedQuotReq?.total ?? q.grandTotal ?? 0;
            const displaySubtotal = matchedQuotReq?.subtotal ?? q.subTotal ?? 0;

            return (
              <div key={q._id} className={cn(cardClassName(), 'p-4 space-y-3 hover:border-blue-300 transition-colors')}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-blue-50 dark:bg-blue-950/40 text-blue-600 rounded-lg">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-base">{displayQuotationNumber}</span>
                        {isSuperseded && (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300 font-semibold">
                            Superseded by {supersededDoc?.supersededByNumber || 'a revised quotation'}
                          </Badge>
                        )}
                        {(matchedReq?.revisedFromNumber || (q as any).revisedFromNumber) && (
                          <Badge className="text-slate-700 border border-slate-300 bg-slate-50 text-[11px] font-mono">
                            Revision of {matchedReq?.revisedFromNumber || (q as any).revisedFromNumber}
                          </Badge>
                        )}
                        {/* Dynamic status badge */}
                        {isDraft ? (
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-300">Draft Quotation</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold">
                            ✓ Quotation Created
                          </Badge>
                        )}
                        {hasBeenEmailed && emailTarget?.emailSentAt && (
                          <Badge className="bg-sky-100 text-sky-800 border border-sky-300 font-semibold">
                            Emailed {new Date(emailTarget.emailSentAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        Client: <strong className="text-foreground">{displayClientName}</strong> · {displayLinesCount} line item(s) · Date: {new Date(matchedReq?.requestedAt || q.date).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-bold text-base font-mono text-foreground">{formatINR(displayGrandTotal)}</div>
                    <div className="text-xs text-muted-foreground">
                      Subtotal: {formatINR(displaySubtotal)}
                    </div>
                  </div>
                </div>

                {/* Revision History — shown when the quotation has been revised */}
                {matchedReq && (matchedReq.documentRevisions?.length > 0 || (matchedReq.currentRevisionNumber && matchedReq.currentRevisionNumber > 1)) && (
                  <details className="group">
                    <summary className="cursor-pointer text-xs font-semibold text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5 select-none hover:text-indigo-900 dark:hover:text-indigo-300 py-1">
                      <History className="h-3.5 w-3.5" />
                      Revision History ({matchedReq.documentRevisions?.length || 0} snapshot{matchedReq.documentRevisions?.length !== 1 ? 's' : ''})
                      <span className="ml-auto text-[10px] text-muted-foreground group-open:hidden">▶ Show</span>
                      <span className="ml-auto text-[10px] text-muted-foreground hidden group-open:inline">▼ Hide</span>
                    </summary>
                    <div className="mt-2 space-y-1.5 pl-1">
                      {(matchedReq.documentRevisions || []).map((rev: any, idx: number) => (
                        <div key={idx} className="flex items-start gap-2 text-xs bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/50 rounded-lg px-3 py-2">
                          <span className="font-bold text-indigo-700 dark:text-indigo-400 shrink-0 mt-0.5">
                            {rev.revisionLabel || `R${idx + 1}`}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-muted-foreground truncate">{rev.note || 'Snapshot taken'}</span>
                              {rev.status && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 shrink-0">
                                  was: {rev.status}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              By {rev.createdByName || 'System'} · {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                              {rev.amount ? ` · ₹${Number(rev.amount).toLocaleString('en-IN')}` : ''}
                            </div>
                          </div>
                        </div>
                      ))}
                      {(matchedReq.documentRevisions || []).length === 0 && (
                        <p className="text-xs text-muted-foreground pl-2">Revision started — no snapshots yet.</p>
                      )}
                    </div>
                  </details>
                )}

                {/* Workflow Action Buttons */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <div className="text-xs text-muted-foreground">
                    {isDraft && 'Draft. Confirm the quotation when it is ready.'}
                    {isIssued && isSuperseded && (!reqStatus?.startsWith('proforma') && reqStatus !== 'sent') && (
                      <span className="font-medium">Superseded by {supersededDoc?.supersededByNumber || 'a revised quotation'} — create the Proforma from that quotation instead.</span>
                    )}
                    {isIssued && !isSuperseded && !hasProforma && (
                      <span className="text-emerald-700 dark:text-emerald-300 font-medium">✓ Quotation created. Click below to create the Proforma Invoice.</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2">

                      {/* If quotation is in draft status, it is editable ONLY by the user who created it */}
                      {isDraft && isDraftOwner(q, matchedQuotReq || matchedReq) && (
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setEditingQuotation({
                              id: matchedQuotReq ? (matchedQuotReq.id || matchedQuotReq._id) : (matchedReq ? matchedReq.id : q._id),
                              data: { ...(matchedQuotReq || matchedReq || q), matchedReqId: matchedQuotReq?.id || matchedReq?.id },
                            });
                          }}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit Draft
                        </Button>
                      )}

                      {/* A draft that was never confirmed is deleted for good, before editing it. */}
                      {isDraft && isDraftOwner(q, matchedQuotReq || matchedReq) && (
                        <Button
                          variant="outline"
                          className="text-rose-700 border-rose-300 hover:bg-rose-50"
                          disabled={actions.deleteDraft.isPending}
                          onClick={() => setPendingDraftDelete({
                            id: String(matchedQuotReq ? (matchedQuotReq.id || matchedQuotReq._id) : (matchedReq ? matchedReq.id : q._id)),
                            number: displayQuotationNumber,
                          })}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Draft
                        </Button>
                      )}

                      <Button variant="outline" className="text-primary border-primary/30 hover:bg-primary/5" onClick={() => handleOpenDrawer(q, matchedQuotReq || matchedReq, 'quotation')}>
                        <Eye className="h-3.5 w-3.5 mr-1.5" /> View Quotation
                      </Button>

                      {/* Confirmed and not replaced: the quotation can go to the client, even once a proforma exists. */}
                      {isIssued && !isSuperseded && emailTarget && (
                        <Button
                          variant="outline"
                          className="text-primary border-primary/30 hover:bg-primary/5"
                          onClick={() => setSendEmailDoc({
                            id: String(emailTarget.id || emailTarget._id),
                            number: displayQuotationNumber,
                            clientEmail: emailTarget.clientEmail || (q as any).customerEmail,
                            clientName: displayClientName,
                          })}
                        >
                          <Mail className="h-3.5 w-3.5 mr-1.5" /> {hasBeenEmailed ? 'Resend Email' : 'Send Email'}
                        </Button>
                      )}

                      {Boolean(matchedProformaReq || matchedReq?.proformaNumber || reqStatus?.startsWith('proforma') || matchedReq?.proformaFullyApproved || matchedReq?.proformaId) && (
                        <Button
                          variant="outline"
                          className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 font-medium"
                          onClick={() => handleOpenDrawer(q, matchedProformaReq || matchedReq, 'proforma')}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1.5 text-indigo-600" /> View Proforma Invoice
                        </Button>
                      )}



                      {/* A draft becomes an active quotation: no approval step. */}
                      {isDraft && (
                        <Button
                          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm"
                          disabled={!q.lines?.length && !matchedReq?.items?.length}
                          onClick={() => convert(q, matchedReq)}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Confirm Quotation
                        </Button>
                      )}

                      {/* Not emailed yet: Edit (same quotation, no new revision). Emailed: Revise (a new quotation).
                          Hidden once a proforma invoice has been created for it. */}
                      {matchedReq && !isSuperseded && !hasTaxInvoice && !isDraft && !matchedProformaReq && !matchedReq.proformaId && !hasRealProformaNumber(matchedReq) && !reqStatus?.startsWith('proforma') && (
                        <Button
                          variant="outline"
                          className="text-blue-800 border-blue-300 hover:bg-blue-50 font-medium"
                          onClick={async () => {
                            const docId = matchedReq.id || matchedReq._id || q._id;
                            if (!docId) return;
                            try {
                              // Opening the form changes nothing: no revision is created until a revised quotation is saved.
                              const { data: freshDoc } = await api.get(`/invoice-requests/${docId}`);
                              const latestDoc = freshDoc?.request || freshDoc?.doc || freshDoc;
                              if (!latestDoc) { toast.error('Could not load latest quotation data.'); return; }
                              startQuotationRevision({ id: latestDoc._id || latestDoc.id, ...latestDoc } as any);
                            } catch (err: any) {
                              toast.error(getApiErrorMessage(err, 'open this quotation'));
                            }
                          }}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1.5" /> {hasBeenEmailed ? 'Revise Quotation' : 'Edit Quotation'}
                        </Button>
                      )}

                      {/* Request Proforma Invoice — create/raise Proforma for this quotation */}
                      {!hasProforma && !reqStatus?.startsWith('proforma') && reqStatus !== 'sent' && !matchedReq?.proformaId && !hasTaxInvoice && !isDraft && !isSuperseded && isIssued && (
                        <Button
                          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm"
                          onClick={async () => {
                            const qId = matchedQuotReq?.id || matchedQuotReq?._id || q.id || q._id || matchedReq?.id || matchedReq?._id;
                            const qNo = displayQuotationNumber || matchedQuotReq?.number || q.number || q.documentNumber || q.quotationNumber || '';

                            let dealData: any = (q as any).fromDeal || (matchedReq as any)?.fromDeal || null;
                            const dealId = (q as any).sourceDealId || (q as any).dealId || (matchedReq as any)?.sourceDealId || (matchedReq as any)?.dealId || (q as any).clientId;
                            if (!dealData && dealId) {
                              try {
                                const res = await api.get(`/api/deals/${dealId}`);
                                dealData = res.data?.deal || res.data?.data || res.data;
                              } catch {}
                            }
                            if (!dealData) {
                              dealData = {
                                clientName: q.customerName || q.clientName || matchedReq?.clientName || '',
                                clientAddress: (q as any).clientAddress || (q as any).customerAddress || (matchedReq as any)?.clientAddress || '',
                                clientGstin: (q as any).clientGstin || (q as any).customerGstin || (matchedReq as any)?.clientGstin || '',
                                contactPerson: (q as any).contactPerson || (matchedReq as any)?.contactPerson || '',
                                email: q.customerEmail || (q as any).clientEmail || (matchedReq as any)?.clientEmail || '',
                                phone: q.customerPhone || (q as any).clientPhone || (matchedReq as any)?.clientPhone || '',
                                services: (q as any).services || [],
                                value: q.total || q.subtotal || 0,
                                notes: q.notes || '',
                              };
                            }
                            navigate(`/app/sales/proforma/create?quotationId=${encodeURIComponent(qId)}&quotationNumber=${encodeURIComponent(qNo)}&dealId=${encodeURIComponent(dealId || '')}`);
                          }}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" /> Create Proforma Invoice
                        </Button>
                      )}

                  </div>
                </div>
              </div>
            );
              })}
            </div>
          )}
        </>
      )}

      {/* Document Lifecycle Table */}
      {activeTab === 'lifecycle' && (
        <div className={cn(cardClassName(), 'overflow-hidden space-y-0')}>
          <div className="px-4 py-3 border-b border-border bg-muted/30 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Document Lifecycle — Quotation (QT) → Proforma (PI) → Tax Invoice (INV) → Receipt
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Tracks full payment contracts &amp; milestone contracts from quotation to receipt.
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-medium">
              <span className="px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200 font-mono font-bold">QT: Quotation</span>
              <span>→</span>
              <span className="px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-950 text-indigo-800 dark:text-indigo-200 font-mono font-bold">PI: Proforma</span>
              <span>→</span>
              <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-200 font-mono font-bold">INV: Tax Invoice</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[850px]">
              <thead className="bg-muted/50 text-xs font-semibold text-muted-foreground border-b">
                <tr>
                  <th className="px-3 py-2.5 text-center w-10 text-muted-foreground font-bold">#</th>
                  <th className="px-4 py-2.5 text-left">Quotation (QT)</th>
                  <th className="px-4 py-2.5 text-left">Client</th>
                  <th className="px-4 py-2.5 text-left">Revisions</th>
                  <th className="px-4 py-2.5 text-left">Activity & History</th>
                  <th className="px-4 py-2.5 text-left">Proforma (PI)</th>
                  <th className="px-4 py-2.5 text-left">Tax Invoice (INV)</th>
                  <th className="px-4 py-2.5 text-left">Payment Status</th>
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {requests.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground text-xs">No quotation lifecycle data yet.</td></tr>
                ) : requests.map((r: any, recIdx: number) => {
                  const revisions = r.documentRevisions || [];
                  const rejections = revisions.filter((rev: any) => rev.status === 'rejected' || rev.rejectedBy);
                  const revisionLabels = revisions.map((_: any, i: number) => `V${i + 1}`).join(', ');
                  const rejectionLabels = rejections.length > 0 ? rejections.map((_: any, i: number) => `R${i + 1}`).join(' ') : '';
                  const hasProforma = hasRealProformaNumber(r) || ['proforma_requested', 'proforma_under_review', 'proforma_approved', 'proforma_rejected', 'sent'].includes(r.status);
                  const hasInvoice = Boolean(r.invoiceNumber || r.invoiceId || r.status === 'sent');
                  const isFullPayment = r.paymentType === 'full_payment' || r.billingType === 'full_payment' || r.paymentSchedule?.billingType === 'full_payment';
                  const isMilestoneContract = !isFullPayment && Boolean(r.paymentSchedule?.milestones?.length || r.isMilestone || r.billingType === 'milestone');

                  // Milestone phase calculation
                  const totalAmt = r.total || r.grandTotal || 0;
                  const milestonePhases = r.paymentSchedule?.milestones?.length ? r.paymentSchedule.milestones.map((m: any, idx: number) => {
                    const isPaid = m.status === 'paid' || (m.amountReceivedMinor && m.amountReceivedMinor >= (m.amountDueMinor || 1));
                    const isPartiallyPaid = m.status === 'partially_paid' || (m.amountReceivedMinor > 0 && m.amountReceivedMinor < (m.amountDueMinor || 1));
                    const isInvGenerated = m.status === 'invoice_generated' || isPaid || isPartiallyPaid || Boolean(m.invoiceNumber);

                    const cleanStr = (str?: string | null) => {
                      if (!str) return '';
                      let s = str.trim();
                      s = s.replace(/\s*\((?:PI|TI)?\s*\([IVXLCDM\d]+\)\)/gi, '');
                      s = s.replace(/\s*\((?:PI|TI)\)/gi, '');
                      s = s.replace(/\([IVXLCDM\d]+\)$/i, '');
                      return s.trim();
                    };

                    // Proforma number: use stored milestone proformaNumber
                    let piNo = m.proformaNumber ? cleanStr(m.proformaNumber) : (r.milestoneIndex === idx && r.proformaNumber ? cleanStr(r.proformaNumber) : '—');

                    // Tax Invoice number: use stored milestone invoiceNumber
                    let invNo = m.invoiceNumber ? cleanStr(m.invoiceNumber) : (r.milestoneIndex === idx && r.invoiceNumber ? cleanStr(r.invoiceNumber) : '—');

                    return {
                      name: m.name || `Phase ${idx + 1}`,
                      pct: m.percentage || (100 / (r.paymentSchedule.milestones.length || 4)),
                      amount: m.amount || (m.amountDueMinor ? m.amountDueMinor / 100 : (totalAmt * (m.percentage || 25)) / 100),
                      piNo,
                      invNo,
                      isInvGenerated,
                      invoiceStatus: isInvGenerated ? 'Generated' : (m.status === 'proforma_approved' || m.status === 'invoice_pending_approval' ? 'Pending Approval' : 'Pending'),
                      isPaid,
                      isPartiallyPaid,
                      paymentStatus: isPaid ? 'Paid' : isPartiallyPaid ? 'Partially Paid' : 'Unpaid',
                    };
                  }) : isFullPayment ? [
                    {
                      name: '100% Full Payment on Order / Delivery',
                      pct: 100,
                      amount: totalAmt,
                      piNo: r.proformaNumber || (hasProforma ? 'PI (Generated)' : '—'),
                      invNo: r.invoiceNumber || (hasInvoice ? 'TI (Generated)' : '—'),
                      isInvGenerated: hasInvoice,
                      invoiceStatus: hasInvoice ? 'Generated' : 'Pending',
                      isPaid: r.invoiceStatus === 'paid',
                      isPartiallyPaid: r.invoiceStatus === 'partially_paid',
                      paymentStatus: r.invoiceStatus === 'paid' ? 'Paid' : r.invoiceStatus === 'partially_paid' ? 'Partially Paid' : 'Unpaid',
                    }
                  ] : [];

                  return (
                    <div key={r.id || r._id} className="contents">
                      <tr className="hover:bg-muted/20 font-medium">
                        <td className="px-3 py-2.5 text-center font-mono font-bold text-slate-500 text-xs">{recIdx + 1}</td>
                        <td className="px-4 py-2.5">
                          <span className="font-mono font-bold text-primary text-xs">{r.number || '—'}</span>
                          {r.currentRevisionLabel && <span className="ml-1 text-[10px] px-1 py-0.5 bg-blue-100 text-blue-700 rounded font-bold">{r.currentRevisionLabel}</span>} 
                        </td>
                        <td className="px-4 py-2.5 text-xs">{r.clientName || '—'}</td>
                        <td className="px-4 py-2.5 text-xs">
                          <span className="font-mono">{revisionLabels || 'V1'}</span>
                          {rejectionLabels && <span className="ml-1 text-rose-600 font-bold">{rejectionLabels}</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          <div className="space-y-1 max-h-24 overflow-y-auto">
                            {r.history && r.history.length > 0 ? (
                              r.history.slice(-3).map((h: any, i: number) => (
                                <div key={i} className="text-[10px] flex items-start gap-1">
                                  <span className="text-slate-400">{new Date(h.at).toLocaleDateString()}</span>
                                  <span className="font-medium text-slate-700">{h.byName || 'System'}</span>
                                  <span className="text-rose-600">→</span>
                                  <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-rose-100 text-rose-700">{h.to?.replace(/_/g, ' ')}</span>
                                  {h.note && <span className="text-slate-500 italic truncate max-w-[150px]">: {h.note}</span>}
                                </div>
                              ))
                            ) : (
                              <span className="text-slate-400 text-[10px]">No activity yet</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {isMilestoneContract && milestonePhases.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {milestonePhases.map((mp: any, i: number) => (
                                <span key={i} className="font-mono font-bold text-[11px] text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded border border-indigo-200 dark:border-indigo-800 w-max" title={mp.name}>
                                  {mp.piNo !== '—' ? mp.piNo : <span className="text-slate-400 font-normal">PI (pending)</span>}
                                </span>
                              ))}
                            </div>
                          ) : hasProforma && r.proformaNumber ? (
                            <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">
                              {r.proformaNumber}
                            </span>
                          ) : <span className="text-muted-foreground font-mono text-[11px]">PI (pending)</span>}
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {isMilestoneContract && milestonePhases.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {milestonePhases.map((mp: any, i: number) => (
                                <span key={i} className={`font-mono font-bold text-[11px] px-1.5 py-0.5 rounded border w-max ${
                                  mp.isInvGenerated
                                    ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800'
                                    : 'text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800 border-dashed border-slate-200 dark:border-slate-700'
                                }`} title={mp.name}>
                                  {mp.invNo !== '—' ? mp.invNo : <span className="font-normal">TI (pending)</span>}
                                </span>
                              ))}
                            </div>
                          ) : hasInvoice && r.invoiceNumber ? (
                            <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                              {r.invoiceNumber}
                            </span>
                          ) : (
                            <span className="text-muted-foreground font-mono text-[11px]">TI (pending)</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs font-bold">
                          {r.invoiceId || hasInvoice ? (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              r.invoiceStatus === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                              r.invoiceStatus === 'partially_paid' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                              'bg-amber-100 text-amber-800 border border-amber-300'
                            }`}>{r.invoiceStatus || 'unpaid'}</span>
                          ) : (
                            <span className="text-muted-foreground font-mono text-[11px]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            r.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                            r.status === 'approved' || r.status === 'proforma_approved' ? 'bg-blue-100 text-blue-700' :
                            r.status === 'rejected' || r.status === 'proforma_rejected' ? 'bg-rose-100 text-rose-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>{r.status?.replace(/_/g, ' ')}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-bold">{formatINR(r.total || 0)}</td>
                      </tr>

                      {/* Embedded Milestone / Full Payment Section Row */}
                      <tr className="bg-slate-50/80 dark:bg-slate-900/50 border-b border-border/80">
                        <td colSpan={10} className="px-5 py-3">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs font-bold text-slate-800 dark:text-slate-200">
                              <span className="flex items-center gap-1.5">
                                <Layers className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                {isFullPayment ? 'Payment Schedule — Full Payment Contract' : 'Milestone Section Breakdown — Proforma (PI) → Tax Invoice (TI)'}
                              </span>
                              <span className="text-slate-500 font-mono text-[11px] font-normal">
                                Format: <strong className="text-indigo-600">PI/2026-27/0001</strong> → <strong className="text-emerald-600">TI/2026-27/0001</strong>
                              </span>
                            </div>

                            <div className={`grid grid-cols-1 ${isFullPayment ? 'max-w-md' : 'sm:grid-cols-2 md:grid-cols-4'} gap-3`}>
                              {milestonePhases.map((m: any, idx: number) => (
                                <div key={idx} className={cn(cardClassName({ variant: 'inset' }), 'p-2.5 space-y-2')}>
                                  <div className="flex items-center justify-between font-bold text-xs">
                                    <span className="truncate text-slate-900 dark:text-slate-100">{m.name}</span>
                                    <span className="font-mono text-purple-600 dark:text-purple-400 text-[11px] bg-purple-50 dark:bg-purple-950 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800">
                                      {m.pct}%
                                    </span>
                                  </div>
                                  
                                  <div className="flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400">
                                    <span>Amount: <strong className="text-slate-900 dark:text-slate-100 font-mono">{formatINR(m.amount)}</strong></span>
                                  </div>

                                  {/* PI → TI lifecycle chain */}
                                  <div className="pt-2 border-t border-slate-100 dark:border-slate-700 space-y-1.5">
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <span className="text-[9px] font-semibold text-indigo-500 uppercase tracking-wide w-6">PI</span>
                                      <span className="font-mono font-bold text-[10px] text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded border border-indigo-200 dark:border-indigo-800 truncate max-w-[120px]" title={m.piNo}>
                                        {m.piNo}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <span className="text-[9px] font-semibold text-emerald-500 uppercase tracking-wide w-6">TI</span>
                                      <span className={`font-mono font-bold text-[10px] px-1.5 py-0.5 rounded border truncate max-w-[120px] ${
                                        m.isInvGenerated
                                          ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800'
                                          : 'text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                      }`} title={m.invNo}>
                                        {m.invNo}
                                      </span>
                                      <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${
                                        m.isInvGenerated ? 'text-emerald-700 bg-emerald-100' :
                                        m.invoiceStatus === 'Pending Approval' ? 'text-amber-700 bg-amber-100' :
                                        'text-slate-500 bg-slate-100'
                                      }`}>
                                        {m.invoiceStatus}
                                      </span>
                                    </div>

                                    <div className="flex items-center justify-end mt-1">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                                        m.isPaid ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                                        m.isPartiallyPaid ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                                        'bg-amber-100 text-amber-800 border border-amber-300'
                                      }`}>
                                        {m.paymentStatus}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    </div>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slide-out Document Drawer */}
      <SalesDocumentWorkspaceDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        document={selectedDocData}
        readOnly
        onSaveHtml={handleSaveDrawerHtml}
        onResubmit={handleDrawerResubmit}
        onReject={handleDrawerReject}
        onOpenResubmissionForm={handleOpenResubmissionForm}
        onReviseQuotation={handleOpenRevisionForm}
        onSendEmail={(doc) => setSendEmailDoc({
          id: doc.id,
          number: doc.number || doc.documentNumber || '',
          clientEmail: doc.clientEmail,
          clientName: doc.clientName || doc.customerName,
        })}
        onEditDraft={(doc) => {
          setDrawerOpen(false);
          if (doc.requestType === 'proforma' || doc.documentType === 'proforma' || (doc as any).proformaNumber) {
            // Edit Proforma opens the same full-page form used for creating,
            // which fetches the document fresh from the API.
            navigate(salesProformaEditPath(doc.id || (doc as any)._id));
          } else {
            setEditingQuotation({
              id: doc.id || (doc as any)._id,
              data: doc,
            });
            smoothScrollToTop();
          }
        }}
        onRequestProforma={async (doc) => {
          setDrawerOpen(false);
          const d = doc as any;
          const qId = d.sourceQuotationId || d.id || d._id;

          const qNum = d.sourceQuotationNumber || d.number || d.quotationNumber || '';
          let dealData = d.fromDeal || d.dealData || null;
          const dealId = d.sourceDealId || d.dealId || d.clientId;
          if (!dealData && dealId) {
            try {
              const res = await api.get(`/api/deals/${dealId}`);
              dealData = res.data?.deal || res.data?.data || res.data;
            } catch {}
          }
          if (!dealData) {
            dealData = {
              clientName: d.clientName || '',
              clientAddress: d.clientAddress || d.billingAddress || '',
              clientGstin: d.clientGstin || '',
              contactPerson: d.contactPerson || '',
              email: d.clientEmail || d.email || '',
              phone: d.clientPhone || d.phone || '',
              services: d.services || [],
              value: d.total || d.subtotal || 0,
              notes: d.notes || '',
            };
          }
          setProformaQuotationId(qId);
          setProformaQuotationNumber(qNum);
          setProformaFromDeal(dealData);
          setShowProformaForm(true);
          smoothScrollToTop();
        }}
      />

      {sendEmailDoc && (
        <SendDocumentEmailModal
          open
          onClose={() => setSendEmailDoc(null)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
            queryClient.invalidateQueries({ queryKey: ['deals'] });
          }}
          documentId={sendEmailDoc.id}
          documentNumber={sendEmailDoc.number}
          requestType="quotation"
          clientEmail={sendEmailDoc.clientEmail}
          clientName={sendEmailDoc.clientName}
        />
      )}

      <ConfirmDialog
        open={Boolean(pendingDraftDelete)}
        onCancel={() => setPendingDraftDelete(null)}
        onConfirm={confirmDraftDelete}
        loading={deletingDraft}
        danger
        confirmLabel="Delete permanently"
        title={`Delete draft ${pendingDraftDelete?.number || 'quotation'}?`}
        message="This draft was never confirmed, so it is removed from the database entirely. The action cannot be undone."
      />
      </div>
    </div>
  );
}
